import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import BetCard from "./BetCard";
import { megaApi } from "../lib/api";

const TABS = ["all", "won", "lost", "cancelled"];
const STATUS = { won: "won", lost: "lost", push: "void", void: "cancelled" };

// Casino round → BetCard shape, so Slot / Live Casino bets render exactly like My Bets.
const toBet = (r, category) => ({
  id: r.round_id,
  kind: "casino",
  ref: `#${String(r.round_id).slice(-8).toUpperCase()}`,
  status: STATUS[r.result] || "void",
  stake: r.stake,
  potentialReturn: r.payout,
  placedAt: r.at,
  sessionName: r.game || "Game",
  matchLabel: `${r.provider || "Casino"} · ${category === "live" ? "Live Casino" : "Slots"}`,
  tag: category === "live" ? "Live" : "Slot",
});

export default function MegaHistory({ category = "slot" }) {
  const [state, setState] = useState({ loading: true, rounds: [], days: 10 });
  const [tab, setTab] = useState("all");
  useEffect(() => {
    let dead = false;
    megaApi.history(category)
      .then((d) => { if (!dead) setState({ loading: false, rounds: d.rounds || [], days: d.retention_days || 10 }); })
      .catch(() => { if (!dead) setState((s) => ({ ...s, loading: false })); });
    return () => { dead = true; };
  }, [category]);

  if (state.loading) return <div className="py-6 grid place-items-center text-slate-400" data-testid={`mega-history-${category}-loading`}><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const bets = state.rounds.map((r) => toBet(r, category));
  const filtered = bets.filter((b) => tab === "all" || b.status === tab || (tab === "cancelled" && b.status === "void"));
  return (
    <div className="space-y-3" data-testid={`mega-history-${category}`}>
      <div className="flex items-center justify-between gap-2">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 h-auto bg-[#111318] ring-1 ring-white/[0.07] border-0 rounded-full p-1" data-testid={`mega-history-${category}-tabs`}>
            {TABS.map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`mega-history-${category}-tab-${t}`} className="capitalize rounded-full px-2.5 py-1.5 min-w-0 truncate text-[11px] font-bold data-[state=active]:bg-[#f5b400] data-[state=active]:text-black">{t}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <span className="text-[10px] text-slate-500 shrink-0" data-testid={`mega-history-${category}-count`}>{filtered.length} · last {state.days} days</span>
      </div>
      {filtered.length === 0 ? (
        <div className="rounded-xl bg-[#111318] ring-1 ring-white/[0.07] p-6 text-center text-sm text-zinc-500" data-testid={`mega-history-${category}-empty`}>
          {bets.length === 0 ? (category === "live" ? "No live casino bets yet. Play any live table and your bets will show here." : "No slot bets yet. Spin any slot and your bets will show here.") : "No bets here yet."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.slice(0, 100).map((b) => <BetCard key={b.id} b={b} compact />)}
        </div>
      )}
    </div>
  );
}
